import style from './style.module.scss'
import { Dispatch, SetStateAction } from 'react'
import classNames from 'classnames'
import { Button, Header, Text } from '@crossfit/design-system'
import badge1 from 'components/Admin/images/L1-BADGE.svg'
import badge2 from 'components/Admin/images/L2-BADGE.svg'
import badge3 from 'components/Admin/images/L3-BADGE.svg'
import badge4 from 'components/Admin/images/L4-BADGE.svg'
import Toggle, { ToggleValue } from '../../../Toggle'
import { AffiliateMember } from 'types'

export const capitalize = (string) => string[0].toUpperCase() + string.slice(1)

type Props = {
  label?: string
  desc?: string
  subDesc?: string
  value?: any
  isEditable?: boolean
  includeToggle?: boolean
  toggleValue?: ToggleValue
  coaches?: AffiliateMember[]
  yearsAffiliated?: any
  includeValueCharacterCount?: boolean
  email?: string
  buttonText?: string
  formOpen?: boolean
  setIsEditing?: Dispatch<SetStateAction<boolean>>
  onToggle?: (value: ToggleValue) => void
}

const formatValue = (label, value) => {
  return label === 'Gender' ? capitalize(value) : value
}

function CoachCredentials({ coaches }: { coaches: AffiliateMember[] }) {
  const findHighestCredForCoach = (coach: AffiliateMember) => {
    const l1L4Creds = ['OL1', 'L1', 'L2', 'L3', 'L4']
    const coachTrainerCreds =
      coach.certifications
        ?.map((c) => c.level)
        .filter((l) => l1L4Creds.includes(l)) ?? []

    if (coachTrainerCreds.includes('L4')) {
      return 'L4'
    }

    if (coachTrainerCreds.includes('L3')) {
      return 'L3'
    }

    if (coachTrainerCreds.includes('L2')) {
      return 'L2'
    }

    if (coachTrainerCreds.includes('L1') || coachTrainerCreds.includes('OL1')) {
      return 'L1'
    }
  }
  const findNumberOfCoachCredsForLevel = (level: string) => {
    return coaches.filter((coach) => findHighestCredForCoach(coach) === level)
  }

  const l1Creds = findNumberOfCoachCredsForLevel('L1')
  const l2Creds = findNumberOfCoachCredsForLevel('L2')
  const l3Creds = findNumberOfCoachCredsForLevel('L3')
  const l4Creds = findNumberOfCoachCredsForLevel('L4')

  return (
    <div className={style.coachCredentialsContainer}>
      <div className={style.credentialItem}>
        <img src={badge1} className={style.badge1} />
        <Text
          type={'body'}
          size={'md'}
          className={style.multipleText}
          bold={true}
        >
          {l1Creds.length > 0 ? '(' + String(l1Creds.length) + 'x)' : '-'}
        </Text>
      </div>
      <div className={style.credentialItem}>
        <img src={badge2} className={style.badgeImage} />
        <Text
          type={'body'}
          size={'md'}
          className={style.multipleText}
          bold={true}
        >
          {l2Creds.length > 0 ? '(' + String(l2Creds.length) + 'x)' : '-'}
        </Text>
      </div>
      <div className={style.credentialItem}>
        <img src={badge3} className={style.badgeImage} />
        <Text
          type={'body'}
          size={'md'}
          className={style.multipleText}
          bold={true}
        >
          {l3Creds.length > 0 ? '(' + String(l3Creds.length) + 'x)' : '-'}
        </Text>
      </div>
      <div className={style.credentialItem}>
        <img src={badge4} className={style.badgeImage} />
        <Text
          type={'body'}
          size={'md'}
          className={style.multipleText}
          bold={true}
        >
          {l4Creds.length > 0 ? '(' + String(l4Creds.length) + 'x)' : '-'}
        </Text>
      </div>
    </div>
  )
}

function YearsAffiliated({ yearsAffiliated }) {
  return (
    <section className={style.openSection}>
      <div className={style.openRow}>
        <div className={style.affiliatedYearsText}>
          <Header as='h4' size='xxl'>
            {yearsAffiliated}
          </Header>
          <Text size='md' type='body' semiBold>
            {yearsAffiliated > 1 ? 'Years' : 'Year'}
          </Text>
          <Text
            size='md'
            className={style.affiliatedText}
            type={'body'}
            semiBold
          >
            Affiliated
          </Text>
        </div>
      </div>
    </section>
  )
}

function Display(props: Props) {
  const {
    label,
    desc,
    subDesc,
    isEditable,
    includeToggle = false,
    toggleValue,
    coaches,
    yearsAffiliated,
    value,
    formOpen,
    setIsEditing,
    onToggle,
  } = props

  return (
    <div className={classNames(style.wrapper, style.wrapperThreeCols)}>
      {label && (
        <Text
          as='div'
          className={classNames(style.col, style.label)}
          type='body'
          size='sm'
        >
          {label}
        </Text>
      )}
      {value != null && coaches == null && yearsAffiliated == null && (
        <>
          <div className={classNames(style.col, style.value)}>
            <div className={style.info}>
              <Text
                as='div'
                className={classNames(style.infoValue, {
                  [style.inactiveValue]: !isEditable && !value,
                })}
                type='body'
                size='sm'
                bold
              >
                {formatValue(label, value)}
              </Text>
              {props.includeValueCharacterCount && (
                <Text
                  type='body'
                  size='sm'
                  color=''
                  as='p'
                  className={style.characterCount}
                >
                  {'Character count ' +
                    String((value as string).length) +
                    '/500'}
                </Text>
              )}
              {desc && (
                <Text as='div' className={style.infoDesc} type='body' size='sm'>
                  {desc}
                </Text>
              )}
              {subDesc && (
                <Text as='div' className={style.infoDesc} type='body' size='sm'>
                  {subDesc}
                </Text>
              )}
            </div>
          </div>
        </>
      )}
      {value == null &&
        coaches == null &&
        yearsAffiliated == null &&
        !isEditable && (
          <>
            <div className={classNames(style.col, style.value)}>
              <div className={style.info}>
                <Text
                  as='div'
                  className={classNames(style.infoValue, {
                    [style.inactiveValue]: !isEditable,
                  })}
                  type='body'
                  size='sm'
                  bold
                >
                  {formatValue(label, 'Not Set')}
                </Text>
                {desc && (
                  <Text
                    as='div'
                    className={style.infoDesc}
                    type='body'
                    size='sm'
                  >
                    {desc}
                  </Text>
                )}
                {subDesc && (
                  <Text
                    as='div'
                    className={style.infoDesc}
                    type='body'
                    size='sm'
                  >
                    {subDesc}
                  </Text>
                )}
              </div>
            </div>
          </>
        )}
      {!coaches && !yearsAffiliated && !value && isEditable ? (
        <div className={classNames(style.col, style.add)}>
          <Button
            variant='secondary'
            color='white'
            className={style.addButton}
            onClick={() => setIsEditing(true)}
          >
            {`Add ${label}`}
          </Button>
        </div>
      ) : null}
      {coaches ? (
        <div className={classNames(style.col, style.change)}>
          <CoachCredentials coaches={coaches} />
        </div>
      ) : null}
      {!coaches && yearsAffiliated ? (
        <div className={classNames(style.col, style.change)}>
          <YearsAffiliated yearsAffiliated={yearsAffiliated} />
        </div>
      ) : null}
      {isEditable ? (
        <div className={classNames(style.col, style.edit)}>
          {value != null && (
            <button
              className={classNames(style.editBtn, {
                [style.formOpen]: formOpen,
              })}
              onClick={() => (formOpen ? null : setIsEditing(true))}
            />
          )}
        </div>
      ) : null}
      {includeToggle && toggleValue != null ? (
        <div className={classNames(style.col, style.toggle)}>
          <Toggle onToggle={onToggle} toggleValue={props.toggleValue} />
        </div>
      ) : null}
    </div>
  )
}

export default Display
